import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  BarChart2,
  Link2,
  PlusCircle,
  TrendingUp,
  Flag,
  Users,
  DollarSign,
  FileText,
  Plug,
  ArrowLeft,
  ChevronDown,
} from 'lucide-react';
import { hasPI, hasPIAccess } from '../../lib/permissions';
import { piApi } from '../../services/piApi';
import PIPortfolioPage from './PIPortfolioPage';
import PILockedPage from './PILockedPage';
import PIPortcoShell from './PIPortcoShell';
import PILPPortal from './PILPPortal';
import PIImportWizard from './PIImportWizard';
import './PIShell.css';

const PI_NAV_ITEMS = [
  { id: 'dashboard',   label: 'Portfolio Dashboard', icon: LayoutDashboard, path: '/pi/dashboard' },
  { id: 'vcp',         label: 'Value Creation',       icon: ClipboardList,   path: null }, // portco-scoped
  { id: 'bridge',      label: 'EBITDA Bridge',         icon: BarChart2,       path: null },
  { id: 'synergies',   label: 'Synergy Tracker',       icon: Link2,           path: null },
  { id: 'addons',      label: 'Add-Ons',                icon: PlusCircle,      path: null },
  { id: 'returns',     label: 'Exit Scenarios',        icon: TrendingUp,      path: null },
  { id: 'exit',        label: 'Exit Readiness',        icon: Flag,            path: null },
  { id: 'talent',      label: 'Leadership & Talent',   icon: Users,           path: null },
  { id: 'cash',        label: 'Cash & Tax',            icon: DollarSign,      path: null },
  { id: 'lp',           label: 'LP Reporting',          icon: FileText,  path: null },
  { id: 'integrations', label: 'Integrations',          icon: Plug,      path: null },
];

export default function PIShell({ currentUser, onBackToDD, lastDDPath }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPortcoId, setSelectedPortcoId] = useState(null);
  const [portcoDropdownOpen, setPortcoDropdownOpen] = useState(false);
  const [portcos, setPortcos] = useState([]);

  // Fetch portcos for the sidebar selector
  useEffect(() => {
    piApi.getPortcos().then(r => {
      const list = r.data || [];
      setPortcos(list);
      if (list.length > 0 && !selectedPortcoId) {
        setSelectedPortcoId(list[0].id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // /pi/dashboard        → segments[2] = 'dashboard'
  // /pi/portco/:id/bridge → segments[2] = 'portco', segments[4] = 'bridge'
  const pathParts = location.pathname.split('/');
  const activeSegment = pathParts[2] === 'portco' ? (pathParts[4] || 'overview') : (pathParts[2] || 'dashboard');

  const handleNavItem = (item) => {
    if (item.path) {
      navigate(item.path);
    } else if (selectedPortcoId) {
      navigate(`/pi/portco/${selectedPortcoId}/${item.id}`);
    } else {
      navigate('/pi/dashboard');
    }
  };

  const handleBackToDD = () => {
    if (onBackToDD) onBackToDD();
    // App.js handles the navigate; if used standalone, fall back
    else navigate(lastDDPath || '/home');
  };

  return (
    <div className="pi-shell">
      {/* ── PI Left Navigation ─────────────────────────────────────── */}
      <aside className="pi-sidebar">
        <div className="pi-sidebar-header">
          <div className="pi-sidebar-logo">
            <TrendingUp size={16} />
          </div>
          <div className="pi-sidebar-wordmark">
            <span className="pi-brand-main">DealNexa</span>
            <span className="pi-brand-sub">Portfolio Intelligence</span>
          </div>
        </div>

        {/* Portco selector */}
        {portcos.length > 0 && (
          <div className="pi-portco-selector" onClick={() => setPortcoDropdownOpen(o => !o)}>
            <span className="pi-portco-name">
              {portcos.find(p => p.id === selectedPortcoId)?.name || 'Select portfolio company'}
            </span>
            <ChevronDown size={14} className={`pi-portco-chevron ${portcoDropdownOpen ? 'open' : ''}`} />
            {portcoDropdownOpen && (
              <div className="pi-portco-dropdown">
                {portcos.map(p => (
                  <div
                    key={p.id}
                    className={`pi-portco-option ${p.id === selectedPortcoId ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPortcoId(p.id);
                      setPortcoDropdownOpen(false);
                      navigate(`/pi/portco/${p.id}/overview`);
                    }}
                  >
                    {p.name}
                    {p.status === 'at_risk' && <span className="pi-portco-at-risk">At Risk</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <nav className="pi-nav">
          {PI_NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeSegment === item.id ||
              (item.id === 'dashboard' && location.pathname === '/pi/dashboard');
            const isPortcoItem = !item.path;
            const disabled = isPortcoItem && !selectedPortcoId;

            return (
              <div
                key={item.id}
                className={`pi-nav-item ${isActive ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && handleNavItem(item)}
                title={disabled ? 'Select a portfolio company first' : item.label}
              >
                <span className="pi-nav-icon"><Icon size={17} strokeWidth={1.5} /></span>
                <span className="pi-nav-label">{item.label}</span>
              </div>
            );
          })}
        </nav>

        {/* Back to DD button */}
        <div className="pi-sidebar-footer">
          <div className="pi-back-button" onClick={handleBackToDD}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            <span>Back to Due Diligence</span>
          </div>
        </div>
      </aside>

      {/* ── PI Main Content ────────────────────────────────────────── */}
      <main className="pi-main">
        <Routes>
          <Route path="/pi/dashboard" element={
            <PIPortfolioPage currentUser={currentUser} portcos={portcos} onSelectPortco={(id) => {
              setSelectedPortcoId(id);
              navigate(`/pi/portco/${id}/overview`);
            }} />
          } />
          <Route path="/pi/portco/:portcoId/*" element={
            <PIPortcoShell
              currentUser={currentUser}
              onPortcoSelect={(id) => setSelectedPortcoId(id)}
            />
          } />
          <Route path="/pi/lp-portal" element={<PILPPortal currentUser={currentUser} />} />
          <Route path="/pi/import" element={<PIImportWizard currentUser={currentUser} />} />
          <Route path="/pi/locked" element={<PILockedPage currentUser={currentUser} />} />
          <Route path="*" element={<Navigate to="/pi/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}
